import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { IconCheck, IconTrash } from './Icons';
import Logs from './Logs';
import { useAuth } from '../contexts/AuthContext';
import './Settings.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const settingsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;
const logoUrl = process.env.PUBLIC_URL + '/logo.png';
const BUCKET = 'company-assets';
const SIGNED_URL_EXPIRY_SEC = 3600;

async function getSignedAssetUrlWithClient(client, storagePath) {
  if (!storagePath?.trim()) return null;
  const { data, error } = await client.storage.from(BUCKET).createSignedUrl(storagePath.trim(), SIGNED_URL_EXPIRY_SEC);
  if (error) return null;
  return data?.signedUrl ?? null;
}

const SETTINGS_NAV = [
  { id: 'general', label: 'General' },
  { id: 'logs', label: 'Logs' },
];

export default function Settings() {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const [subTab, setSubTab] = useState('general');
  const [contentTab, setContentTab] = useState('general-settings');
  const [stamps, setStamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      settingsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setSaveError(null);
    try {
      await runWakeUp();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        settingsSupabase.from('company_document_assets').select('*').order('created_at', { ascending: true }),
        timeoutPromise,
      ]);
      const { data: rows, error } = res;
      if (error) {
        setSaveError(error.message);
        setStamps([]);
        setLoading(false);
        return;
      }
      const list = rows ?? [];
      const stampRows = list.filter((r) => r.type === 'stamp');
      if (stampRows.length > 0 && !stampRows.some((r) => r.is_default)) {
        await settingsSupabase.from('company_document_assets').update({ is_default: true }).eq('id', stampRows[0].id);
      }
      const listAfterRes = await settingsSupabase.from('company_document_assets').select('*').order('created_at', { ascending: true });
      const listAfter = listAfterRes.data ?? [];
      const stampRowsFinal = listAfter.filter((r) => r.type === 'stamp');
      const stampUrls = await Promise.all(
        stampRowsFinal.map((r) => getSignedAssetUrlWithClient(settingsSupabase, r.storage_path)),
      );
      setStamps(
        stampRowsFinal.map((r, i) => ({
          id: r.id,
          name: r.name ?? '',
          isDefault: !!r.is_default,
          previewUrl: stampUrls[i] || logoUrl,
          storage_path: r.storage_path,
        })),
      );
    } catch (e) {
      setSaveError(e?.message || 'Failed to load');
      setStamps([]);
    } finally {
      setLoading(false);
    }
  }, [runWakeUp]);

  const refreshSessionAndFetch = useCallback(async () => {
    setSaveError(null);
    try {
      await settingsSupabase.auth.getSession();
      await runWakeUp();
      await fetchAssets();
    } catch (e) {
      setSaveError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchAssets]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { error: sessionErr } = await settingsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setSaveError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchAssets();
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchAssets]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const addStamp = () => {
    setStamps((prev) => [
      ...prev,
      {
        id: `draft-stamp-${Date.now()}`,
        name: `Company stamp ${prev.length + 1}`,
        isDefault: prev.length === 0,
        previewUrl: logoUrl,
        isDraft: true,
      },
    ]);
  };

  const uploadStampFile = async (draftId, file) => {
    if (!userId) return;
    await runWakeUp();
    const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '');
    const id = crypto.randomUUID?.() ?? `stamp-${Date.now()}`;
    const path = `${userId}/stamp/${id}.${ext || 'png'}`;
    const { error: uploadErr } = await settingsSupabase.storage.from(BUCKET).upload(path, file, {
      upsert: true,
      contentType: file.type || 'image/png',
    });
    if (uploadErr) {
      setSaveError(uploadErr.message);
      return;
    }
    const stamp = stamps.find((s) => s.id === draftId);
    const name = stamp?.name ?? 'Company stamp';
    const isFirst = stamps.filter((s) => !s.isDraft).length === 0;
    const { error: insertErr } = await settingsSupabase.from('company_document_assets').insert({
      id,
      type: 'stamp',
      name,
      storage_path: path,
      is_default: isFirst,
    });
    if (insertErr) {
      setSaveError(insertErr.message);
      return;
    }
    setStamps((prev) =>
      prev.map((s) => (s.id === draftId ? { ...s, id, isDraft: false, previewUrl: null } : s)),
    );
    await fetchAssets();
  };

  const replaceStampImage = async (id, file) => {
    const stamp = stamps.find((s) => s.id === id && s.storage_path);
    if (!stamp?.storage_path) return;
    await runWakeUp();
    const { error } = await settingsSupabase.storage.from(BUCKET).upload(stamp.storage_path, file, {
      upsert: true,
      contentType: file.type || 'image/png',
    });
    if (!error) await fetchAssets();
    else setSaveError(error.message);
  };

  const updateStampName = async (id, name) => {
    if (String(id).startsWith('draft-')) {
      setStamps((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
      return;
    }
    await runWakeUp();
    const { error } = await settingsSupabase
      .from('company_document_assets')
      .update({ name })
      .eq('id', id)
      .eq('type', 'stamp');
    if (!error) setStamps((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
    else setSaveError(error.message);
  };

  const setDefaultStamp = async (id) => {
    if (String(id).startsWith('draft-')) return;
    await runWakeUp();
    await settingsSupabase.from('company_document_assets').update({ is_default: false }).eq('type', 'stamp');
    await settingsSupabase.from('company_document_assets').update({ is_default: true }).eq('id', id);
    await fetchAssets();
  };

  const deleteStamp = async (id) => {
    if (String(id).startsWith('draft-')) {
      setStamps((prev) => {
        const next = prev.filter((s) => s.id !== id);
        if (next.length && !next.some((s) => s.isDefault)) next[0] = { ...next[0], isDefault: true };
        return next;
      });
      return;
    }
    const stamp = stamps.find((s) => s.id === id);
    const wasDefault = stamp?.isDefault;
    await runWakeUp();
    const { data } = await settingsSupabase.from('company_document_assets').select('storage_path').eq('id', id).single();
    if (data?.storage_path) await settingsSupabase.storage.from(BUCKET).remove([data.storage_path]);
    await settingsSupabase.from('company_document_assets').delete().eq('id', id);
    if (wasDefault) {
      const { data: remaining } = await settingsSupabase.from('company_document_assets').select('id').eq('type', 'stamp').limit(1);
      if (remaining?.[0]?.id) {
        await settingsSupabase.from('company_document_assets').update({ is_default: true }).eq('id', remaining[0].id);
      }
    }
    await fetchAssets();
  };

  const handleSaveSettings = () => {
    setSaveError(null);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  return (
    <div className="settings-page">
      <aside className="settings-nav">
        <h2 className="settings-nav-title">App Settings</h2>
        <nav className="settings-nav-list">
          {SETTINGS_NAV.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`settings-nav-item ${subTab === item.id ? 'active' : ''}`}
              onClick={() => setSubTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      <main className="settings-main">
        <div className="settings-tabs">
          <button
            type="button"
            className={`settings-tab ${contentTab === 'general-settings' ? 'active' : ''}`}
            onClick={() => setContentTab('general-settings')}
          >
            General Settings
          </button>
        </div>

        {subTab === 'general' && contentTab === 'general-settings' && (
          <div className="settings-form">
            {saveError && (
              <p className="settings-field-description" style={{ color: 'var(--danger, #b91c1c)', marginBottom: '0.75rem' }}>
                {saveError}
              </p>
            )}
            {loading ? (
              <p className="settings-field-description">Loading stamps and signatures…</p>
            ) : (
              <>
                <div className="settings-field">
                  <label>Company stamps</label>
                  <p className="settings-field-description">
                    Choose one default stamp to be used on documents (e.g. PDFs).
                  </p>
                  <div>
                    {stamps.length === 0 && (
                      <p className="settings-field-description" style={{ marginBottom: '0.5rem' }}>
                        No stamps yet. Add one and upload an image.
                      </p>
                    )}
                    {stamps.map((stamp) => (
                      <div
                        key={stamp.id}
                        className="settings-field-row"
                        style={{ marginBottom: '0.75rem' }}
                      >
                        <div className="settings-logo-preview settings-logo-small">
                          <img src={stamp.previewUrl || logoUrl} alt="Company stamp" />
                        </div>
                        <div className="settings-buttons">
                          <input
                            className="settings-input-inline"
                            value={stamp.name}
                            onChange={(e) => updateStampName(stamp.id, e.target.value)}
                            onBlur={(e) => updateStampName(stamp.id, e.target.value)}
                            placeholder="Stamp name"
                          />
                          <label className="settings-btn-outline" style={{ cursor: 'pointer' }}>
                            {stamp.isDraft ? 'Upload (required)' : 'Replace image'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                e.target.value = '';
                                if (stamp.isDraft) uploadStampFile(stamp.id, file);
                                else replaceStampImage(stamp.id, file);
                              }}
                            />
                          </label>
                          <label className="settings-checkbox-row">
                            <input
                              type="radio"
                              name="defaultStamp"
                              checked={stamp.isDefault}
                              disabled={stamps.length === 1}
                              onChange={() => setDefaultStamp(stamp.id)}
                            />
                            <span>Default</span>
                          </label>
                          <button
                            type="button"
                            className="settings-delete-btn"
                            onClick={() => deleteStamp(stamp.id)}
                            aria-label="Delete stamp"
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="settings-btn-outline"
                      onClick={addStamp}
                    >
                      + Add stamp
                    </button>
                  </div>
                </div>

                <div className="settings-field">
                  <label>Authorized signatures</label>
                  <p className="settings-field-description">
                    Signatures are now managed per user in their profile (Team &gt; Members &gt; Member details).
                    The signature used on documents comes from the profile of the logged-in user.
                  </p>
                </div>
              </>
            )}

            <div className="settings-form-actions">
              <button
                type="button"
                className="settings-btn-save"
                onClick={handleSaveSettings}
              >
                <IconCheck />
                Save
              </button>
            </div>
          </div>
        )}

        {subTab === 'logs' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Logs />
          </div>
        )}

        {showToast && (
          <div className="settings-toast">
            Saved
          </div>
        )}

      </main>
    </div>
  );
}
